import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAccount } from '../contexts/AccountContext';
import { apiErrorMessage } from '../utils/apiError';
import { useAuth } from '../contexts/AuthContext';
import IntegrationLogsTab from './IntegrationLogsTab';
import integrationsService from '../services/integrationsService';
import PipelinesHeader from './pipelines/PipelinesHeader';
import ProductSupportTab from './support/ProductSupportTab';
import Toggle from './shared/Toggle';
import PipelineHotelsTab from './pipelines/PipelineHotelsTab';
import { pipelinesTabSearch, pipelinesTabFromSearch } from './pipelines/pipelinesTabs';

// Pipelines — lists the pipeline templates of the partner / chain selected in the navbar. A template belongs to
// a chain (V524); the hotels it runs at are its implementations, so the hotel in the navbar does not narrow the
// list. Rows still carrying a hotel scope are ones V524 could not move (their hotel has no chain).
const scopeLabel = (row) => {
    if (row.mcp_source_system_id) return { label: 'MCP source', name: row.mcp_source_name, color: '#be185d', bg: '#fdf2f8' };
    if (row.hotel_id) return { label: 'Hotel', name: row.hotel_name, color: '#0e7490', bg: '#ecfeff' };
    if (row.account_id) return { label: 'Chain', name: row.account_name, color: '#6d28d9', bg: '#f5f3ff' };
    return { label: 'Partner', name: row.partner_name, color: '#b45309', bg: '#fffbeb' };
};

// EITHER END CAN BE SEVERAL SYSTEMS. One proxy can route to two backends, and an integration can have more
// than one source — so these are lists, not names. Shown as separate chips rather than one comma-joined
// string: "Opera Cloud via OHIP, Azure BLOB Storage" reads as one long name at a glance, and the row is
// scanned rather than read.
//
// THE FIRST TWO, THEN A COUNT. A property with six backends would otherwise make one row as tall as the
// rest of the table; the full list is in the tooltip and on the integration itself.
const SYSTEM_CHIP = {
    display: 'inline-block', padding: '0.1rem 0.45rem', borderRadius: 999,
    background: '#f1f5f9', color: '#334155', fontSize: '0.78rem', fontWeight: 600,
};

const systemCell = (value) => {
    const names = String(value || '').split(',').map(x => x.trim()).filter(Boolean);
    if (!names.length) return <span style={{ color: '#9ca3af' }}>Not drawn yet</span>;
    const shown = names.slice(0, 2);
    const rest = names.length - shown.length;
    return (
        <span style={{ display: 'inline-flex', flexWrap: 'wrap', gap: 4 }} title={names.join('\n')}>
            {shown.map(n => <span key={n} style={SYSTEM_CHIP}>{n}</span>)}
            {rest > 0 && <span style={{ ...SYSTEM_CHIP, background: 'none', color: '#6b7280' }}>+{rest} more</span>}
        </span>
    );
};

// `hotelId` replaces the navbar scope with the pipelines that run at that one hotel (an implementation
// there), and `embedded` drops the page header, the create button and the Logs tab — the hotel's Products
// tab uses this same list. Create stays on the Pipelines page, where the chain it lands on is the navbar's.
//
// `mcpSource` ({ id, name }) is Admin › Platform › MCP as a Service › a source system (V644): the templates built for
// that PMS, which belong to it and to no partner, chain or hotel. The navbar plays no part, there is no Logs tab, and
// create and open carry ?from=mcp-source&mcpSource=<id> so the editor saves the template to the system and Back
// returns here.
const IntegrateSystemsPage = ({ hotelId = null, embedded = false, mcpSource = null }) => {
    const navigate = useNavigate();
    const { selectedPartner, selectedAccount, selectedHotel } = useAccount();
    const { isSuperAdmin } = useAuth();
    // THE TAB IS IN THE URL, so a link to the logs opens on the logs and Back returns to the tab it left.
    // Logs are superadmin-only on the server, so the tab is offered only where it can load.
    //
    // HOTELS FIRST (2026-10-27): the page opens on the hotels running pipelines — each one a hotel + a template, opened
    // as its own page (/pipelines/:templateId/implementations/:implementationId). The templates are their own tab.
    // Embedded (a hotel's Products tab) and an MCP source system's page show the template list alone, as before.
    const [searchParams, setSearchParams] = useSearchParams();
    const pageTabs = !embedded && !mcpSource;
    const requested = searchParams.get('tab');
    const tab = !pageTabs ? 'integrations' : pipelinesTabFromSearch(requested, isSuperAdmin);
    const setTab = (t) => setSearchParams(pipelinesTabSearch(t), { replace: true });
    // DISABLED TEMPLATES (V693) ARE HIDDEN until asked for — in the URL on the Pipelines page, like the tab.
    const [localShowDisabled, setLocalShowDisabled] = useState(false);
    const showDisabled = pageTabs ? searchParams.get('disabled') === '1' : localShowDisabled;
    const setShowDisabled = (on) => (pageTabs
        ? setSearchParams({ ...pipelinesTabSearch(tab), ...(on ? { disabled: '1' } : {}) }, { replace: true })
        : setLocalShowDisabled(on));
    const [toggling, setToggling] = useState(null);   // the template id being switched
    const [integrations, setIntegrations] = useState(null); // null = not loaded
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    // A CHAIN IS CHOSEN FIRST, AND UNTIL IT IS THERE IS NO LIST.
    //
    // Same rule as the Universal API screen: the top bar owns the scope, and a list assembled before the
    // reader has said which chain they mean is either somebody else's work or an empty box that reads as
    // "there are none". Nothing is fetched until the chain is set.
    const chainChosen = !!mcpSource || !!hotelId || !!selectedAccount;
    // The hotel the Support tab lists tickets for: the one this page is embedded for, else the navbar's.
    const navbarHotelId = hotelId || (selectedHotel && (selectedHotel.hotelid || selectedHotel.id || selectedHotel)) || null;
    const editorQuery = mcpSource ? `from=mcp-source&mcpSource=${mcpSource.id}` : 'from=pipelines';
    // A TEMPLATE BELONGS TO THE CHAIN (V524); the hotels it runs at are its implementations. So the chain
    // is all this screen needs — the hotel in the top bar does not narrow the list or the create.

    const load = useCallback(async () => {
        if (!mcpSource && !hotelId && (!selectedPartner || !selectedAccount)) { setIntegrations([]); return; }
        setLoading(true);
        setError(null);
        try {
            const res = await integrationsService.getAllIntegrations(mcpSource
                ? { mcp_source_system_id: mcpSource.id }
                : hotelId
                ? { implementation_hotel_id: hotelId }
                : { partner_id: selectedPartner, account_id: selectedAccount || undefined });
            setIntegrations(res.integrations || []);
        } catch (err) {
            setError(apiErrorMessage(err, 'Failed to load integrations'));
            setIntegrations(null);
        } finally {
            setLoading(false);
        }
    }, [mcpSource, hotelId, selectedPartner, selectedAccount]);

    useEffect(() => { load(); }, [load]);

    // ENABLE / DISABLE writes at once. Disabling is refused while a hotel runs the template active; the server's
    // answer names them, and it is shown as it is.
    const setEnabled = async (row, on) => {
        setToggling(row.id);
        setError(null);
        try {
            await integrationsService.updateIntegration(row.id, { enabled: on });
            setIntegrations(list => list.map(r => (r.id === row.id ? { ...r, enabled: on ? 1 : 0 } : r)));
        } catch (err) {
            setError(apiErrorMessage(err, `${row.name} could not be ${on ? 'enabled' : 'disabled'}.`));
        } finally {
            setToggling(null);
        }
    };
    const isEnabled = (row) => Number(row.enabled ?? 1) === 1;
    const disabledCount = (integrations || []).filter(r => !isEnabled(r)).length;
    const shown = (integrations || []).filter(r => showDisabled || isEnabled(r));

    return (
        // THE LOGS TAKE THE WHOLE WIDTH: a record opens into the five-column pipeline board, which a 1100 px column cramped
        // and cut off on the right. The other tabs are short tables and keep the reading width.
        <div style={embedded ? undefined : { padding: '2rem', ...(tab === 'logs' ? {} : { maxWidth: 1100 }) }}>
            {!embedded && <PipelinesHeader
                title={mcpSource ? `${mcpSource.name || 'Source system'} pipelines` : 'Pipelines'}
                // A source system's pipelines have no tab bar, so no Support tab: they keep the button.
                support={mcpSource ? () => ({ product: 'MCP', screen: 'mcp-source-pipelines', mcpSourceSystemId: mcpSource.id }) : undefined}
                subtitle={mcpSource
                        ? `Pipeline templates for ${mcpSource.name || 'this system'}, built for MCP as a Service. They belong to the system, not to any chain or hotel yet.`
                        : tab === 'support'
                        ? 'Support tickets about pipelines at the hotel or chain chosen in the top bar. A hotel\'s own pipeline lists its tickets on its Support tab.'
                        : tab === 'logs'
                        ? 'Every step every integration has taken, newest first. Expand a row for the message\'s whole journey.'
                        : tab === 'hotels' && chainChosen
                        ? 'The hotels running pipelines — each one a pipeline template at a hotel. Open one to set its credentials, move its version and read its logs.'
                        : tab === 'hotels'
                        ? 'Choose a chain to see the hotels running its pipelines.'
                        : chainChosen
                        ? 'Pipeline templates for the selected chain. Open one to add the hotels it runs at on its Implementations tab.'
                        : 'Choose a chain to see and create its pipeline templates.'}
                // Disabled rather than hidden: a control that vanishes leaves someone looking for it, where a greyed
                // one with a reason answers the question on the spot.
                actions={tab === 'integrations' && <button
                        type="button"
                        disabled={!chainChosen}
                        title={chainChosen ? undefined : 'Choose a chain in the top bar first — a pipeline template belongs to a chain'}
                        onClick={() => navigate(`/pipeline-templates/create?${editorQuery}`)}
                        style={{
                            flexShrink: 0,
                            display: 'inline-flex', alignItems: 'center', gap: 6,
                            padding: '0.45rem 0.9rem', fontSize: '0.875rem', fontWeight: 600,
                            color: '#fff', background: chainChosen ? '#4a6cf7' : '#c7d2fe', border: 'none',
                            borderRadius: 7, cursor: chainChosen ? 'pointer' : 'not-allowed',
                        }}
                    >
                        <svg width="14" height="14" viewBox="0 0 16 16" fill="none"><path d="M8 3v10M3 8h10" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/></svg>
                        New Pipeline Template
                    </button>}
                tab={pageTabs ? tab : undefined}
                onTabChange={setTab}
            />}

            {tab === 'support' ? (
                !navbarHotelId && !selectedAccount ? (
                    <div style={{ color: '#6b7280' }}>Choose a chain in the top bar to see its pipeline tickets.</div>
                ) : (
                    <ProductSupportTab product="Pipelines" hotelId={navbarHotelId || undefined}
                        accountId={navbarHotelId ? undefined : selectedAccount}
                        noun={navbarHotelId ? 'pipelines at this hotel' : 'pipelines in this chain'}
                        context={() => ({ screen: 'pipelines', ...(navbarHotelId ? { hotelId: navbarHotelId } : { accountId: selectedAccount }) })} />
                )
            ) : tab === 'logs' ? <IntegrationLogsTab /> : tab === 'hotels' ? (
                !chainChosen ? (
                    <div style={{ background: '#f8fafc', border: '1px solid #e5e7eb', borderRadius: 10, padding: '1.5rem', color: '#6b7280', fontSize: '0.925rem' }}>
                        Choose a chain in the top bar to see the hotels running its pipelines and add more.
                    </div>
                ) : (
                    <PipelineHotelsTab partnerId={selectedPartner} accountId={selectedAccount} hotelId={selectedHotel} />
                )
            ) : (<>

            {/* NO CHAIN, NO LIST. Shown instead of the table, never as an empty one — "No integrations found"
                for a question nobody has answered yet reads as a fact about the chain. */}
            {!chainChosen ? (
                <div style={{ background: '#f8fafc', border: '1px solid #e5e7eb', borderRadius: 10, padding: '1.5rem', color: '#6b7280', fontSize: '0.925rem' }}>
                    Choose a chain in the top bar. Pipeline templates belong to a chain; the hotels each one runs
                    at are added on its Implementations tab.
                </div>
            ) : error && integrations === null ? (
                <div style={{ background: '#fef2f2', border: '1px solid #fecaca', color: '#dc2626', borderRadius: 8, padding: '0.75rem 1rem', fontSize: '0.9rem' }}>{error}</div>
            ) : integrations === null || loading ? (
                <p style={{ color: '#6b7280', fontSize: '0.9rem' }}>Loading integrations…</p>
            ) : (<>
            {/* A switch that was refused (a template still active at a hotel) says why above the list it was clicked in. */}
            {error && (
                <div style={{ background: '#fef2f2', border: '1px solid #fecaca', color: '#dc2626', borderRadius: 8, padding: '0.75rem 1rem', fontSize: '0.9rem', marginBottom: '0.75rem' }}>{error}</div>
            )}
            {disabledCount > 0 && (
                <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: '0.75rem' }}>
                    <Toggle size="sm" on={showDisabled} onChange={setShowDisabled}
                        onLabel={`Showing ${disabledCount} disabled`} offLabel={`${disabledCount} disabled hidden`}
                        title={showDisabled ? 'Click to hide disabled templates' : 'Click to show disabled templates'} />
                </div>
            )}
            {shown.length === 0 ? (
                <div style={{ background: '#f8fafc', border: '1px solid #e5e7eb', borderRadius: 10, padding: '1.5rem', color: '#6b7280', fontSize: '0.925rem' }}>
                    {integrations.length ? 'Every pipeline template here is disabled.'
                        : mcpSource ? `No pipeline templates for ${mcpSource.name || 'this system'} yet.`
                        : hotelId ? 'No pipelines run at this hotel.' : 'No integrations found for the current selection.'}
                </div>
            ) : (
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.9rem' }}>
                    <thead>
                        <tr style={{ background: '#f8fafc', borderBottom: '1px solid #e5e7eb', textAlign: 'left' }}>
                            <th style={{ padding: '0.6rem 0.75rem', color: '#374151', fontWeight: 600 }}>Name</th>
                            {/* SOURCES AND TARGETS, THE WORDS THE DESIGNER USES — not From and To.
                                A pipeline is not one-way: a proxy calls the Universal API and reads the
                                answer back, and the same system routinely appears on both sides, so a
                                row reading "From: Opera Cloud · To: Opera Cloud" looked like a mistake
                                rather than a description. These are the columns of the board the row
                                links to, named the same way, so the list and the diagram agree. */}
                            <th style={{ padding: '0.6rem 0.75rem', color: '#374151', fontWeight: 600 }}
                                title="The systems that send — the Sources column of the design">Sources</th>
                            <th style={{ padding: '0.6rem 0.75rem', color: '#374151', fontWeight: 600 }}
                                title="Where the routes land — the Targets column of the design">Targets</th>
                            <th style={{ padding: '0.6rem 0.75rem', color: '#374151', fontWeight: 600 }}>Scope</th>
                            <th style={{ padding: '0.6rem 0.75rem', color: '#374151', fontWeight: 600 }}
                                title="A disabled template is hidden here and takes no new hotel. It can only be disabled once no hotel runs it active.">Status</th>
                        </tr>
                    </thead>
                    <tbody>
                        {shown.map((row, i) => {
                            const scope = scopeLabel(row);
                            return (
                                <tr
                                    key={row.id}
                                    // THE ROW OPENS THE INTEGRATION. The name was the only thing on it that
                                    // looked like a subject, and nothing on the row was clickable — so the
                                    // list could be read and not used.
                                    onClick={() => navigate(`/pipeline-templates/${row.id}/edit?${editorQuery}`)}
                                    onKeyDown={(e) => {
                                        if (e.key === 'Enter' || e.key === ' ') {
                                            e.preventDefault();
                                            navigate(`/pipeline-templates/${row.id}/edit?${editorQuery}`);
                                        }
                                    }}
                                    tabIndex={0}
                                    role="button"
                                    title={`Open ${row.name}`}
                                    style={{ borderBottom: '1px solid #f3f4f6', background: i % 2 === 0 ? '#fff' : '#f9fafb', cursor: 'pointer',
                                        ...(isEnabled(row) ? {} : { color: '#9ca3af' }) }}
                                >
                                    <td style={{ padding: '0.6rem 0.75rem', color: isEnabled(row) ? '#111827' : '#9ca3af', fontWeight: 600 }}>{row.name}</td>
                                    {/* A DESIGNED INTEGRATION HAS NO CONNECTOR PAIR (V469): it names systems on a
                                        canvas, so the ends come from the design — the Sources on one side, and
                                        on the other whatever each route's credential is bound to. "Not drawn
                                        yet" is only reached by an integration nobody has opened in the
                                        designer, which is a real state and not a null. */}
                                    <td style={{ padding: '0.6rem 0.75rem', color: '#374151' }}>
                                        {systemCell(row.from_connector_name || row.design_from)}
                                    </td>
                                    <td style={{ padding: '0.6rem 0.75rem', color: '#374151' }}>
                                        {systemCell(row.to_connector_name || row.design_to)}
                                    </td>
                                    <td style={{ padding: '0.6rem 0.75rem' }}>
                                        <span style={{ display: 'inline-block', padding: '0.15rem 0.55rem', borderRadius: 999, fontSize: '0.78rem', fontWeight: 600, color: scope.color, background: scope.bg }}>
                                            {scope.label}{scope.name ? ` · ${scope.name}` : ''}
                                        </span>
                                    </td>
                                    {/* The switch is not the row's link: a click or key on it stays here. */}
                                    <td style={{ padding: '0.6rem 0.75rem' }} onClick={(e) => e.stopPropagation()} onKeyDown={(e) => e.stopPropagation()}>
                                        <Toggle size="sm" on={isEnabled(row)} busy={toggling === row.id}
                                            onChange={(on) => setEnabled(row, on)} onLabel="Enabled" offLabel="Disabled"
                                            title={isEnabled(row) ? 'Click to disable — only once no hotel runs it active' : 'Click to enable'} />
                                    </td>
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
            )}
            </>)}
            </>)}
        </div>
    );
};

export default IntegrateSystemsPage;
